import {
  PointOfInterest,
  PointOfInterestTemplate,
  TemplateFieldTypeEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import {
  BLISTER_HEIGHT_LABEL,
  BLISTER_TYPE,
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_LABEL,
  PIT_DEPTH_LABEL,
  UNCERTAIN_PROFILE_LABEL,
} from '@/constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

export const usePoiTemplateFields = ({ data }: { data: PointOfInterest }) => {
  const unitConversionSystem = useAtomValue(state.unitSystem);

  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();
  const templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined = useMemo(() => {
    return allPointOfInterestTemplates?.allPointOfInterestTemplate.find(
      ({ id }) => id === data.template?.id
    );
  }, [allPointOfInterestTemplates, data]);

  const templateFields = useMemo(() => {
    return (
      templateOfSelectedPoi?.fields?.filter(
        (field) => field.name !== 'Comments' && field.displayInReview
      ) || []
    );
  }, [templateOfSelectedPoi]);

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
  }> = useMemo(() => {
    const height = data.deckRelativeHeight;
    const heightValue =
      localiseMeasurement({
        unitSystem: unitConversionSystem,
        value: height,
        sourceUnit: UnitsEnum.Meter,
        roundToDecimal: 1,
        fallbackLabel: UNCERTAIN_PROFILE_LABEL,
      }) ?? UNCERTAIN_PROFILE_LABEL;

    const additionalFieldsData = [
      { label: 'Height from deck', value: heightValue ?? NOT_APPLICABLE_LABEL },
      {
        label: PAINT_BLOCK_LABEL,
        value: data.paintRegion?.name ?? NOT_APPLICABLE_LABEL,
      },
    ];
    if (data.blister) {
      const { maxHeight, pdrHeight, wallLoss, residualCorrosionAllowance } = data.blister || {};
      const label = data.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL;
      const blisterValue =
        maxHeight === 0
          ? UNCERTAIN_PROFILE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: maxHeight || undefined,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              fallbackLabel: UNCERTAIN_PROFILE_LABEL,
              showUnitLabel: true,
            }) ?? UNCERTAIN_PROFILE_LABEL;

      const PdrHeightValue =
        maxHeight === 0
          ? UNCERTAIN_PROFILE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: pdrHeight || undefined,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: true,
            });
      const blisterFields = [
        {
          value: blisterValue,
          label: `${label}`,
        },
        ...(residualCorrosionAllowance
          ? [
              {
                value:
                  localiseMeasurement({
                    unitSystem: unitConversionSystem,
                    value: residualCorrosionAllowance?.value,
                    sourceUnit: UnitsEnum.Meter,
                    displayAsUnit: UnitsEnum.Millimeter,
                    showUnitLabel: true,
                  }) + formatPercentage(residualCorrosionAllowance?.percentage),
                label: 'Residual corrosion allowance',
              },
            ]
          : []),
        ...(pdrHeight
          ? [
              {
                value: PdrHeightValue,
                label: 'PDR Height',
              },
            ]
          : []),
        {
          value:
            localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: wallLoss?.value,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: true,
            }) + formatPercentage(wallLoss?.percentage),
          label: 'Wall loss est',
        },
      ];

      const templateFieldsData =
        templateFields?.map((field) => {
          const value =
            data.templateData?.find((td) => td.id === field.id)?.value ?? NOT_APPLICABLE_LABEL;

          return {
            value,
            label: field?.name || NOT_APPLICABLE_LABEL,
            type: field?.type,
          };
        }) || [];

      return [...blisterFields, ...additionalFieldsData, ...templateFieldsData];
    }

    return [
      ...(templateFields?.map((field) => {
        const value =
          data.templateData?.find((td) => td.id === field.id)?.value ?? NOT_APPLICABLE_LABEL;

        return {
          value,
          label: field?.name || NOT_APPLICABLE_LABEL,
          type: field?.type,
        };
      }) || []),
      ...additionalFieldsData,
    ];
  }, [
    data.deckRelativeHeight,
    data.paintRegion?.name,
    data.blister,
    data.type,
    data.templateData,
    unitConversionSystem,
    templateFields,
  ]);
  return {
    templateFieldsWithValues,
    templateOfSelectedPoi: templateOfSelectedPoi,
  };
};
